// The treatment: what makes anybody's photo look like it belongs on a wall
// in his town.
//
// This is the whole answer to "random images". An upload is never shown as
// itself; it is pushed through a fixed pipeline that ends in one of our
// palettes, so a phone snap of a kitchen and a scan of a drawing come out
// speaking the same language. The knobs are real (people should be able to
// play) but they cannot reach outside the house look: every one of them
// changes HOW the image lands in the palette, never whether it does.
//
// Deliberately plain pixel maths on a Uint8ClampedArray rather than a WebGL
// shader. It has to run in a browser for the live preview and again wherever
// the final texture is baked, and the same arithmetic in both places is the
// only way a saved recipe re-renders to the same poster months later. At
// 512x768 the whole pass is under 400k pixels and costs a few milliseconds.
//
// Everything here is a pure function of (pixels, params, seed). No clock, no
// randomness that is not seeded.

// Ramps run dark to light. Luminance picks a position, the ramp says what
// colour that position is, so the source image only ever supplies structure.
export const PALETTES = {
  dusk: {
    label: 'dusk',
    stops: ['#1b1636', '#3b2f6b', '#7d4fa8', '#f0b064', '#f7e7d2'],
  },
  sodium: {
    label: 'sodium',
    stops: ['#140f0c', '#5a1e12', '#c2521c', '#f0a03c', '#ffe6b0'],
  },
  riso: {
    label: 'riso',
    stops: ['#171a4a', '#2f4bc4', '#e8478f', '#f7a8c4', '#faf0e2'],
  },
  moss: {
    label: 'moss',
    stops: ['#10201a', '#234b36', '#5c8f4e', '#a8d6a0', '#f2f0d8'],
  },
  ink: {
    label: 'ink',
    stops: ['#201c2e', '#3f3a52', '#7a7490', '#c3bcd0', '#f7e7d2'],
  },
}

export const DEFAULT_TREAT = {
  palette: 'dusk',
  levels: 5,      // posterise steps, 2 to 8
  contrast: 1.15, // 0.6 to 2
  lift: 0,        // -0.25 to 0.25, pushes the whole thing up or down the ramp
  halftone: 0,    // 0 to 1, dot pattern strength
  grain: 0.12,    // 0 to 0.5
  invert: false,
  seed: 1,
}

const hex = (h) => {
  const n = parseInt(h.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

/** Ramp lookup with a linear blend between stops, so bands stay smooth. */
function rampAt(stops, t) {
  const clamped = t <= 0 ? 0 : t >= 1 ? 1 : t
  const span = (stops.length - 1) * clamped
  const i = Math.min(stops.length - 2, Math.floor(span))
  const f = span - i
  const a = stops[i]
  const b = stops[i + 1]
  return [
    a[0] + (b[0] - a[0]) * f,
    a[1] + (b[1] - a[1]) * f,
    a[2] + (b[2] - a[2]) * f,
  ]
}

/** Deterministic value noise. Same seed, same grain, forever. */
function noiseAt(x, y, seed) {
  let n = (x * 374761393 + y * 668265263 + seed * 1442695040888963407) | 0
  n = (n ^ (n >> 13)) * 1274126177
  return (((n ^ (n >> 16)) >>> 0) % 1000) / 1000 - 0.5
}

/**
 * Rewrite pixels in place.
 *
 * @param {Uint8ClampedArray} data RGBA, length w*h*4
 */
export function treatPixels(data, w, h, params = {}) {
  const p = { ...DEFAULT_TREAT, ...params }
  const stops = (PALETTES[p.palette] || PALETTES.dusk).stops.map(hex)
  const levels = Math.max(2, Math.min(8, Math.round(p.levels)))
  const seed = Math.max(1, Math.round(p.seed || 1))

  // Halftone grid is scaled to the poster, not to the pixel count, so the
  // dots stay the same size whatever resolution this runs at.
  const cell = Math.max(3, Math.round(w / 90))

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4

      // Rec. 709 luminance: the structure we keep from the original.
      let l = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255

      l = (l - 0.5) * p.contrast + 0.5 + p.lift
      if (p.invert) l = 1 - l

      if (p.halftone > 0) {
        // A dot grid that grows as the tone darkens. Modulating luminance
        // rather than painting dots keeps it inside the palette.
        const cx = (x % cell) - cell / 2
        const cy = (y % cell) - cell / 2
        const d = Math.sqrt(cx * cx + cy * cy) / (cell / 2)
        l += (d - (1 - l)) * 0.5 * p.halftone
      }

      if (p.grain > 0) l += noiseAt(x, y, seed) * p.grain

      // Posterise last, so every knob above lands ON a band rather than
      // between two of them. This is what stops it looking like a filter.
      const band = Math.round(Math.max(0, Math.min(1, l)) * (levels - 1)) / (levels - 1)

      const [r, g, b] = rampAt(stops, band)
      data[i] = r
      data[i + 1] = g
      data[i + 2] = b
      data[i + 3] = 255
    }
  }
  return data
}

/** Clamp a loose object to the ranges above. Used on the way into storage. */
export function cleanTreat(t = {}) {
  const num = (v, lo, hi, dflt) => {
    const n = Number(v)
    return Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : dflt
  }
  return {
    palette: PALETTES[t.palette] ? t.palette : DEFAULT_TREAT.palette,
    levels: Math.round(num(t.levels, 2, 8, DEFAULT_TREAT.levels)),
    contrast: num(t.contrast, 0.6, 2, DEFAULT_TREAT.contrast),
    lift: num(t.lift, -0.25, 0.25, DEFAULT_TREAT.lift),
    halftone: num(t.halftone, 0, 1, DEFAULT_TREAT.halftone),
    grain: num(t.grain, 0, 0.5, DEFAULT_TREAT.grain),
    invert: !!t.invert,
    seed: Math.round(num(t.seed, 1, 9999, 1)),
  }
}
